import { PlusOutlined } from '@ant-design/icons';
import { CONSENT_CHANNELS, CONSENT_PURPOSES, type ConsentChannel, type ConsentPurpose } from '@nks/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Radio,
  Select,
  Skeleton,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { formatDateTime, orDash } from '../../lib/format';
import { captureConsent, listConsents, type ConsentRecord } from './api';

const { Text } = Typography;

/** Latest capture per purpose wins; undefined means the customer was never asked. */
export function currentConsent(
  items: readonly ConsentRecord[],
): Record<ConsentPurpose, ConsentRecord | undefined> {
  const state = {} as Record<ConsentPurpose, ConsentRecord | undefined>;
  for (const purpose of CONSENT_PURPOSES) {
    // The API returns newest first, so the first match is the current decision.
    state[purpose] = items.find((item) => item.purpose === purpose);
  }
  return state;
}

function DecisionTag({ granted }: { granted: boolean }): ReactNode {
  const { t } = useTranslation();
  return (
    <Tag color={granted ? 'green' : 'red'}>
      {granted ? t('customers.consent.granted') : t('customers.consent.withdrawn')}
    </Tag>
  );
}

function channelLabel(t: (key: string) => string, channel: string): string {
  // A channel captured before the list was fixed renders as stored.
  return (CONSENT_CHANNELS as readonly string[]).includes(channel)
    ? t(`customers.consent.channels.${channel}`)
    : channel;
}

export function ConsentTab({ customerUid }: { customerUid: string }): ReactNode {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['customer', customerUid, 'consents'],
    queryFn: () => listConsents(customerUid),
  });

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }
  if (isError || data === undefined) {
    return (
      <Card>
        <Flex vertical gap={8} align="flex-start">
          <Text type="danger">{t('customers.consent.loadFailed')}</Text>
          <Button onClick={() => void refetch()}>{t('common.retry')}</Button>
        </Flex>
      </Card>
    );
  }

  const current = currentConsent(data.items);

  return (
    <Flex vertical gap={16}>
      <Card
        title={t('customers.consent.currentTitle')}
        extra={
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => {
              setRecording(true);
            }}
          >
            {t('customers.consent.record')}
          </Button>
        }
      >
        <Flex vertical gap={12}>
          <Text type="secondary">{t('customers.consent.rule')}</Text>
          {CONSENT_PURPOSES.map((purpose) => {
            const latest = current[purpose];
            return (
              <Flex key={purpose} justify="space-between" align="center" wrap gap={8}>
                <Text strong>{t(`customers.consent.purposes.${purpose}`)}</Text>
                {latest === undefined ? (
                  <Tag>{t('customers.consent.notAsked')}</Tag>
                ) : (
                  <Flex gap={8} align="center" wrap>
                    <DecisionTag granted={latest.granted} />
                    <Text type="secondary">
                      {t('customers.consent.via', {
                        channel: channelLabel(t, latest.channel),
                        when: formatDateTime(latest.capturedAt),
                      })}
                    </Text>
                  </Flex>
                )}
              </Flex>
            );
          })}
        </Flex>
      </Card>

      <Card title={t('customers.consent.historyTitle')}>
        {data.truncated && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('customers.consent.truncated', { count: data.items.length })}
          />
        )}
        {data.items.length === 0 ? (
          <Text type="secondary">{t('customers.consent.empty')}</Text>
        ) : (
          <>
            <div className="nks-list-table">
              <Table<ConsentRecord>
                rowKey="uid"
                size="middle"
                pagination={false}
                dataSource={data.items}
                columns={[
                  {
                    title: t('customers.consent.when'),
                    dataIndex: 'capturedAt',
                    render: (v: string) => formatDateTime(v),
                  },
                  {
                    title: t('customers.consent.purpose'),
                    dataIndex: 'purpose',
                    render: (v: string) => t(`customers.consent.purposes.${v}`),
                  },
                  {
                    title: t('customers.consent.decision'),
                    dataIndex: 'granted',
                    render: (v: boolean) => <DecisionTag granted={v} />,
                  },
                  {
                    title: t('customers.consent.channel'),
                    dataIndex: 'channel',
                    render: (v: string) => channelLabel(t, v),
                  },
                  {
                    title: t('customers.consent.evidence'),
                    dataIndex: 'evidence',
                    render: (v: string | null) => orDash(v),
                  },
                  {
                    title: t('customers.consent.capturedBy'),
                    dataIndex: 'capturedByName',
                    render: (v: string | null) => orDash(v),
                  },
                ]}
              />
            </div>
            <div className="nks-list-cards">
              <Flex vertical gap={8}>
                {data.items.map((item) => (
                  <Card key={item.uid} size="small" styles={{ body: { padding: 12 } }}>
                    <Flex vertical gap={4}>
                      <Flex justify="space-between" align="center" gap={8}>
                        <Text strong>{t(`customers.consent.purposes.${item.purpose}`)}</Text>
                        <DecisionTag granted={item.granted} />
                      </Flex>
                      <Text type="secondary">
                        {t('customers.consent.via', {
                          channel: channelLabel(t, item.channel),
                          when: formatDateTime(item.capturedAt),
                        })}
                      </Text>
                      {item.evidence !== null && <Text>{item.evidence}</Text>}
                      {item.capturedByName !== null && (
                        <Text type="secondary">
                          {t('customers.consent.recordedBy', { name: item.capturedByName })}
                        </Text>
                      )}
                    </Flex>
                  </Card>
                ))}
              </Flex>
            </div>
          </>
        )}
      </Card>

      <RecordConsentDrawer
        customerUid={customerUid}
        open={recording}
        onClose={() => {
          setRecording(false);
        }}
      />
    </Flex>
  );
}

interface ConsentFormValues {
  purpose: ConsentPurpose;
  channel: ConsentChannel;
  granted: boolean;
  evidence?: string;
}

export function RecordConsentDrawer({
  customerUid,
  open,
  onClose,
}: {
  customerUid: string;
  open: boolean;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ConsentFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setFailure(null);
    }
  }, [open, form]);

  const submit = async (): Promise<void> => {
    let values: ConsentFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    setFailure(null);
    try {
      const evidence = values.evidence?.trim() ?? '';
      await captureConsent(customerUid, {
        purpose: values.purpose,
        channel: values.channel,
        granted: values.granted,
        ...(evidence.length > 0 ? { evidence } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: ['customer', customerUid, 'consents'] });
      void message.success(t('customers.consent.saved'));
      onClose();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : t('customers.consent.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionDrawer
      open={open}
      title={t('customers.consent.record')}
      submitLabel={t('customers.consent.record')}
      submitting={submitting}
      onSubmit={() => void submit()}
      onClose={onClose}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
      consequence={t('customers.consent.appendOnly')}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="purpose"
          label={t('customers.consent.purpose')}
          rules={[{ required: true, message: t('customers.consent.purposeRequired') }]}
        >
          <Radio.Group size="large">
            <Flex vertical gap={8}>
              {CONSENT_PURPOSES.map((p) => (
                <Radio key={p} value={p}>
                  {t(`customers.consent.purposes.${p}`)}
                </Radio>
              ))}
            </Flex>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="granted"
          label={t('customers.consent.decision')}
          rules={[{ required: true, message: t('customers.consent.decisionRequired') }]}
        >
          <Radio.Group size="large" optionType="button" buttonStyle="solid">
            <Radio value={true}>{t('customers.consent.granted')}</Radio>
            <Radio value={false}>{t('customers.consent.withdrawn')}</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="channel"
          label={t('customers.consent.channelQuestion')}
          rules={[{ required: true, message: t('customers.consent.channelRequired') }]}
        >
          <Select
            size="large"
            options={CONSENT_CHANNELS.map((c) => ({
              value: c,
              label: t(`customers.consent.channels.${c}`),
            }))}
          />
        </Form.Item>
        <Form.Item
          name="evidence"
          label={t('customers.consent.evidence')}
          extra={t('customers.consent.evidenceHelp')}
          rules={[{ max: 255 }]}
        >
          <Input size="large" />
        </Form.Item>
      </Form>
    </ActionDrawer>
  );
}
